import {
  isDisableZeroReportEnabled,
  isZeroReportType,
  parseFilingFieldRequirementsFromSchedule,
  scheduleFlagToBoolean,
} from "../models/filingScheduleAdminTypes";

/**
 * Helpers for No Client Action Required (Control Panel actionType=no_action)
 * vs No Report Required (workspace status). Keep these concepts separate.
 */

export const NO_ACTION_REQUIRED_MARKER = 'No Action Required';

/** Same labels the Notice of State Report type select persists. */
export const PREMIUM_BEARING_REPORT_TYPE = 'Premium-Bearing';
export const ZERO_REPORT_TYPE = 'Zero Report';

export type NoActionScheduleMapping = Record<string, any> | null;

export interface NoActionScheduleByType {
  premiumBearing: NoActionScheduleMapping;
  zeroReport: NoActionScheduleMapping;
  disableZeroReport?: boolean | number | string;
}

export interface ResolvedNoActionMapping {
  typeOfReport: string;
  instructions: string;
  notesToClient: string;
  taxAmount: string;
  reportInvoiceRequired: boolean;
  reconciliationListRequired: boolean;
  taxRequired: boolean;
  instructionsRequired: boolean;
  additionalNotesRequired: boolean;
}

function mappingText(mapping: NoActionScheduleMapping, ...keys: string[]): string {
  if (!mapping) return '';
  for (const key of keys) {
    const value = mapping[key];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

function mappingTaxAmount(mapping: NoActionScheduleMapping): string {
  if (!mapping) return '';
  const taxAmount = mapping.tax_Amount ?? mapping.Tax_Amount ?? null;
  return taxAmount == null ? '' : String(taxAmount).trim();
}

function mappingActionType(mapping: NoActionScheduleMapping): string {
  return String(mapping?.actionType ?? mapping?.ActionType ?? '').trim().toLowerCase();
}

/** Mappings with no actionType are already scoped to NCAR. Notice mappings are not offered here. */
function isSelectableNoActionMapping(mapping: NoActionScheduleMapping): boolean {
  if (!mapping) return false;
  const actionType = mappingActionType(mapping);
  return actionType === '' || actionType === 'no_action';
}

function isZeroReportLabel(typeOfReport: string): boolean {
  const normalized = typeOfReport.trim().toLowerCase();
  return normalized === 'zero report' || normalized === 'zero-report' || isZeroReportType(typeOfReport);
}

function isPremiumBearingLabel(typeOfReport: string): boolean {
  const normalized = typeOfReport.trim().toLowerCase();
  return normalized === 'premium-bearing' || normalized === 'premium bearing';
}

/** Mapping row for a Type of Report, including Notice rows. */
function selectScheduleMapping(
  schedule: NoActionScheduleByType | null | undefined,
  typeOfReport: string,
): NoActionScheduleMapping {
  if (!schedule) return null;
  if (isPremiumBearingLabel(typeOfReport)) return schedule.premiumBearing ?? null;
  if (isZeroReportLabel(typeOfReport)) {
    if (isDisableZeroReportEnabled(schedule.disableZeroReport)) return null;
    return schedule.zeroReport ?? null;
  }
  return null;
}

/** Zero Report exists on the schedule and is not disabled (Notice or NCAR). */
export function canFileScheduleAsZeroReport(
  schedule: NoActionScheduleByType | null | undefined,
): boolean {
  if (!schedule?.zeroReport) return false;
  return !isDisableZeroReportEnabled(schedule.disableZeroReport);
}

/**
 * Types shown on File Now and Filed Notes: any configured mapping.
 * Notice vs NCAR is decided after the user picks a type.
 */
export function listScheduleReportTypeOptions(
  schedule: NoActionScheduleByType | null | undefined,
): string[] {
  if (!schedule) return [];
  const options: string[] = [];
  if (schedule.premiumBearing) options.push(PREMIUM_BEARING_REPORT_TYPE);
  if (canFileScheduleAsZeroReport(schedule)) options.push(ZERO_REPORT_TYPE);
  return options;
}

/** True when File Now would keep this type on Notice of State Report (not NCAR). */
export function isNoticeActionType(
  schedule: NoActionScheduleByType | null | undefined,
  typeOfReport: string | null | undefined,
): boolean {
  const selected = String(typeOfReport ?? '').trim();
  if (!selected) return false;
  const mapping = selectScheduleMapping(schedule, selected);
  if (!mapping) return false;
  return mappingActionType(mapping) !== 'no_action';
}

/** Zero Report is offered only when that mapping exists and the schedule has not disabled it. */
export function canFileNoActionAsZeroReport(
  schedule: NoActionScheduleByType | null | undefined,
): boolean {
  if (!schedule?.zeroReport || !isSelectableNoActionMapping(schedule.zeroReport)) return false;
  return !isDisableZeroReportEnabled(schedule.disableZeroReport);
}

/** Premium-Bearing, plus Zero Report when the schedule allows it. Never invents a default. */
export function listNoActionReportTypeOptions(
  schedule: NoActionScheduleByType | null | undefined,
): string[] {
  if (!schedule) return [];
  const options: string[] = [];
  if (isSelectableNoActionMapping(schedule.premiumBearing)) options.push(PREMIUM_BEARING_REPORT_TYPE);
  if (canFileNoActionAsZeroReport(schedule)) options.push(ZERO_REPORT_TYPE);
  return options;
}

/**
 * The only selectable type, or empty when the user must choose.
 * Does not fall back to premium when both types exist.
 */
export function soleNoActionReportType(
  schedule: NoActionScheduleByType | null | undefined,
): string {
  const options = listNoActionReportTypeOptions(schedule);
  return options.length === 1 ? options[0] : '';
}

function selectNoActionMapping(
  schedule: NoActionScheduleByType,
  typeOfReport: string,
): NoActionScheduleMapping {
  if (isPremiumBearingLabel(typeOfReport)) {
    return isSelectableNoActionMapping(schedule.premiumBearing) ? schedule.premiumBearing : null;
  }
  if (isZeroReportLabel(typeOfReport)) {
    if (!canFileNoActionAsZeroReport(schedule)) return null;
    return schedule.zeroReport;
  }
  return null;
}

/**
 * Control Panel instructions, client message, tax, and required flags for the chosen type.
 * Empty or unknown selection returns null — it does not default to the premium mapping.
 */
export function resolveNoActionMapping(
  schedule: NoActionScheduleByType | null | undefined,
  typeOfReport: string | null | undefined,
): ResolvedNoActionMapping | null {
  if (!schedule) return null;
  const selected = String(typeOfReport ?? '').trim();
  if (!selected) return null;
  const mapping = selectNoActionMapping(schedule, selected);
  if (!mapping) return null;
  const requirements = parseFilingFieldRequirementsFromSchedule(mapping);
  const canonicalType = isZeroReportType(selected)
    ? ZERO_REPORT_TYPE
    : PREMIUM_BEARING_REPORT_TYPE;
  return {
    typeOfReport: canonicalType,
    instructions: mappingText(mapping, 'instructions', 'Instructions'),
    notesToClient: mappingText(
      mapping,
      'additional_Notes',
      'Additional_Notes',
      'additionalNotes',
    ),
    taxAmount: mappingTaxAmount(mapping),
    reportInvoiceRequired: scheduleFlagToBoolean(
      mapping.reportInvoiceRequired
        ?? mapping.report_Invoice_Required_Y_N
        ?? mapping.Report_Invoice_Required_Y_N,
    ),
    reconciliationListRequired: scheduleFlagToBoolean(
      mapping.reconciliationListRequired
        ?? mapping.reconciliation_List_Required_Y_N
        ?? mapping.Reconciliation_List_Required_Y_N,
    ),
    taxRequired: requirements.taxAmount,
    instructionsRequired: requirements.instructions,
    additionalNotesRequired: requirements.additionalNotes,
  };
}

/** True when the report was completed via the No Client Action Required Filed Notes path. */
export function isNoClientActionRequiredComplete(
  additionalNotes: string | null | undefined,
  status?: string | null | undefined,
): boolean {
  const notes = String(additionalNotes ?? '').trim();
  if (notes !== NO_ACTION_REQUIRED_MARKER) return false;
  if (status == null || String(status).trim() === '') return true;
  return String(status).trim() === 'Filed';
}

/** Preview dialog title for NCA vs notice File Now. */
export function getStateReportPreviewTitle(
  additionalNotes: string | null | undefined,
  status?: string | null | undefined,
): string {
  if (isNoClientActionRequiredComplete(additionalNotes, status)) {
    return 'Report Completed On Your Behalf';
  }
  return 'Notice of State Report';
}

/**
 * Client-facing Additional Notes: hide the internal workflow marker.
 * Prefer notesToClient (Control Panel Additional_Notes) when present.
 */
export function getClientFacingAdditionalNotes(
  additionalNotes: string | null | undefined,
  notesToClient?: string | null | undefined,
): string {
  const marker = String(additionalNotes ?? '').trim();
  const clientNotes = String(notesToClient ?? '').trim();
  if (marker === NO_ACTION_REQUIRED_MARKER) {
    return clientNotes;
  }
  return marker;
}

/**
 * NCA Filed Notes persist Additional Notes only.
 * The internal Note box is stored on `notes`, never appended here.
 */
export function resolveNcaNotesToClient(
  additionalNotes: string | null | undefined,
  mappingFallback?: string | null | undefined,
): string {
  return String(additionalNotes ?? mappingFallback ?? '').trim();
}

/** Whether Filed Notes should show the attachment picker (NCA layout only, never NRR). */
export function shouldShowFiledNotesAttachments(
  openFiledNotes: boolean,
  targetStatus: string | null | undefined,
): boolean {
  if (!openFiledNotes) return false;
  return String(targetStatus ?? '').trim() !== 'No Report Required';
}

export const FILED_NOTES_REPORT_OR_INVOICE = 'Report or Invoice';
export const FILED_NOTES_RECONCILIATION_LIST = 'Reconciliation List';

export function isFiledNotesSlotRequired(
  slotType: string,
  reportInvoiceRequired = false,
  reconciliationListRequired = false,
): boolean {
  if (slotType === FILED_NOTES_REPORT_OR_INVOICE) return !!reportInvoiceRequired;
  if (slotType === FILED_NOTES_RECONCILIATION_LIST) return !!reconciliationListRequired;
  return false;
}

export function areFiledNotesRequiredSlotsFilled(
  slots: Array<{ type: string; file: File | null }>,
  reportInvoiceRequired = false,
  reconciliationListRequired = false,
): boolean {
  if (reportInvoiceRequired && !slots.some((s) => s.type === FILED_NOTES_REPORT_OR_INVOICE && s.file)) {
    return false;
  }
  if (reconciliationListRequired && !slots.some((s) => s.type === FILED_NOTES_RECONCILIATION_LIST && s.file)) {
    return false;
  }
  return true;
}

export const PREMIUM_TAX_AMOUNT_PLACEHOLDER = 'e.g. 3, $300, See Attached Schedule';
export const ZERO_TAX_AMOUNT_PLACEHOLDER = "Enter '0' for Zero Reports";

/** Tax Amount empty-state copy follows Type of Report (Control Panel premium hint vs zero). */
export function getFiledNotesTaxAmountPlaceholder(
  typeOfReport: string | null | undefined,
): string {
  return isZeroReportType(typeOfReport)
    ? ZERO_TAX_AMOUNT_PLACEHOLDER
    : PREMIUM_TAX_AMOUNT_PLACEHOLDER;
}

/** True when Tax Amount meets the Control Panel Required toggle for NCA Filed Notes. */
export function isFiledNotesTaxAmountSatisfied(
  value: string | null | undefined,
  taxRequired = false,
): boolean {
  if (!taxRequired) return true;
  return String(value ?? "").trim() !== "";
}

/** Confirm copy before File Now publish or NCA Filed Notes complete. */
export function getStateReportPublishConfirmText(
  clientName: string | null | undefined,
): string {
  const name = String(clientName ?? '').trim() || 'the client';
  return `Are You Sure you want to publish to ${name}?`;
}
